import {
  Close,
  Content,
  Description,
  Overlay,
  Portal,
  Root,
  Title,
  Trigger,
} from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { ComponentProps, FC } from "react";

import { cn } from "@/lib/utils";

type SheetProps = ComponentProps<typeof Root>;
type SheetTriggerProps = ComponentProps<typeof Trigger>;
type SheetCloseProps = ComponentProps<typeof Close>;
type SheetPortalProps = ComponentProps<typeof Portal>;
type SheetOverlayProps = ComponentProps<typeof Overlay>;
type SheetContentProps = ComponentProps<typeof Content> & {
  side?: "top" | "right" | "bottom" | "left";
};
type SheetHeaderProps = ComponentProps<"div">;
type SheetFooterProps = ComponentProps<"div">;
type SheetTitleProps = ComponentProps<typeof Title>;
type SheetDescriptionProps = ComponentProps<typeof Description>;

export const Sheet: FC<SheetProps> = (props) => (
  <Root data-slot="sheet" {...props} />
);

export const SheetTrigger: FC<SheetTriggerProps> = (props) => (
  <Trigger data-slot="sheet-trigger" {...props} />
);

export const SheetClose: FC<SheetCloseProps> = ({ ...props }) => (
  <Close data-slot="sheet-close" {...props} />
);

const SheetPortal: FC<SheetPortalProps> = (props) => (
  <Portal data-slot="sheet-portal" {...props} />
);

const SheetOverlay: FC<SheetOverlayProps> = ({ className, ...props }) => (
  <Overlay
    data-slot="sheet-overlay"
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
      className,
    )}
    {...props}
  />
);

export const SheetContent: FC<SheetContentProps> = ({
  className,
  children,
  side = "right",
  ...props
}) => (
  <SheetPortal>
    <SheetOverlay />
    <Content
      data-slot="sheet-content"
      className={cn(
        "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
        side === "right" &&
          "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
        side === "left" &&
          "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
        side === "top" &&
          "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
        side === "bottom" &&
          "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
        className,
      )}
      {...props}
    >
      {children}
      <Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
        <XIcon className="size-4" />
        <span className="sr-only">Close</span>
      </Close>
    </Content>
  </SheetPortal>
);

export const SheetHeader: FC<SheetHeaderProps> = ({ className, ...props }) => (
  <div
    data-slot="sheet-header"
    className={cn("flex flex-col gap-1.5 p-4", className)}
    {...props}
  />
);

export const SheetFooter: FC<SheetFooterProps> = ({ className, ...props }) => (
  <div
    data-slot="sheet-footer"
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);

export const SheetTitle: FC<SheetTitleProps> = ({ className, ...props }) => (
  <Title
    data-slot="sheet-title"
    className={cn("text-foreground font-medium tracking-tight", className)}
    {...props}
  />
);

export const SheetDescription: FC<SheetDescriptionProps> = ({
  className,
  ...props
}) => (
  <Description
    data-slot="sheet-description"
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
);
